import React from 'react';

// 2026-08-27 owner report ("ADMIN from the sidenav shows a black page"): root cause --
// AdminPanel (and every other route below) is React.lazy()-loaded as its own hashed JS
// chunk. A tab left open across a server redeploy still holds a reference to the OLD
// chunk filename; the server only ever serves the LATEST build's files (Vite's dist is
// overwritten each deploy, old hashes are gone), so the dynamic import() 404s the moment
// the user finally navigates to that route. With no error boundary anywhere in the tree,
// React just silently rendered nothing -- the "black page" was that failed import with
// zero user-facing feedback (would've been a console error only, easy to miss).
//
// Fix: wrap every Suspense'd lazy route in this boundary. A chunk-load failure is
// specifically named (Vite/webpack both throw "Failed to fetch dynamically imported
// module" / "ChunkLoadError") and is ALWAYS fixed by a real page reload (fetches the
// current index.html, which references the current chunk hashes) -- so it recovers
// automatically, once, rather than surfacing a dead end. Any other render crash still
// shows a real error screen instead of a black one.
const CHUNK_ERROR_PATTERN = /Failed to fetch dynamically imported module|ChunkLoadError|Loading chunk \d+ failed|error loading dynamically imported module/i;

export default class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    if (CHUNK_ERROR_PATTERN.test(error?.message || '')) {
      // One auto-reload per browser session -- guards against a genuine outage (server
      // actually down) turning into a reload loop.
      const key = 'aetheris_chunk_reload_attempted';
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        window.location.reload();
      }
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    const isChunkError = CHUNK_ERROR_PATTERN.test(this.state.error?.message || '');
    return (
      <div style={{
        minHeight: '100vh', background: '#050508', color: '#00ffff',
        fontFamily: 'monospace', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: '1rem', padding: '2rem', textAlign: 'center',
      }}>
        <div style={{ fontSize: '1.1rem', letterSpacing: '1px' }}>
          {isChunkError ? '// RELOADING — A NEWER VERSION IS AVAILABLE' : '// SOMETHING WENT WRONG'}
        </div>
        {!isChunkError && (
          <div style={{ color: '#88aabb', fontSize: '0.85rem', maxWidth: '480px' }}>
            This screen hit an unexpected error. Reloading usually fixes it.
          </div>
        )}
        <button
          onClick={() => window.location.reload()}
          style={{
            background: '#00ffff', color: '#000', border: 'none', padding: '0.6rem 1.5rem',
            fontFamily: 'monospace', fontWeight: 'bold', cursor: 'pointer', letterSpacing: '1px',
          }}
        >
          ⟳ RELOAD NOW
        </button>
      </div>
    );
  }
}
